<template>
	<div class="wrapper" data-testid="order-summary">
		<div class="order-summary">
			<div
				v-if="modifyingCart"
				class="loading-skeleton"
				aria-label="Loading"
				aria-busy="true"
				aria-live="polite"
			>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item"
				></div>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item --half"
				></div>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item --half"
				></div>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item --half"
				></div>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item --half"
				></div>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item --half"
				></div>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item --half"
				></div>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item --half"
				></div>
				<div
					aria-hidden="true"
					role="presentation"
					class="loading-skeleton__item --half"
				></div>
			</div>

			<template v-else>
				<div class="order-summary__products">
					<div
						v-for="item in items"
						:key="item.id"
						class="order-summary__product"
					>
						<div
							v-if="item.image"
							class="order-summary__img-container"
						>
							<img :src="item.image" :alt="item.name" />
						</div>

						<div class="order-summary__product-details">
							<span class="order-summary__product-name"
								>{{ item.name }} x {{ item.quantity }}</span
							>
							<span class="order-summary__price">{{
								item.price
							}}</span>
						</div>
					</div>
				</div>

				<dl class="order-summary__details">
					<template v-if="coupons.length">
						<dt class="order-summary__item">Coupons</dt>
						<dd class="order-summary__item">
							<span v-for="coupon in coupons" :key="coupon.id">{{
								coupon.price
							}}</span>
						</dd>
					</template>
					<dt class="order-summary__item">Subtotal</dt>
					<dd class="order-summary__item">
						{{ priceBreakdown.subTotal }}
					</dd>
					<template v-if="!isDigitalProduct">
						<dt class="order-summary__item">Shipping</dt>
						<dd
							class="order-summary__item"
							:class="{
								'--muted': !priceBreakdown.shipping,
							}"
						>
							{{ priceBreakdown.shipping || '-' }}
						</dd>
					</template>
					<dt class="order-summary__item">Tax</dt>
					<dd
						class="order-summary__item"
						:class="{ '--muted': !priceBreakdown.tax }"
					>
						{{ taxLabel }}
					</dd>
					<dt class="order-summary__item --total">Total</dt>
					<dd class="order-summary__item --total">
						<span>
							<span class="order-summary__currency">{{
								priceBreakdown.currency
							}}</span>
							<span>{{ priceBreakdown.total }}</span>
						</span>
					</dd>
				</dl>
			</template>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
	computed: {
		...mapState('checkout', ['modifyingCart']),
		...mapGetters('checkout', [
			'items',
			'coupons',
			'priceBreakdown',
			'isDigitalProduct',
		]),
		taxLabel() {
			if (this.priceBreakdown.tax) {
				return this.priceBreakdown.tax;
			}

			return this.isDigitalProduct
				? 'calculated after information'
				: 'calculated after shipping';
		},
	},
};
</script>

<style lang="scss" scoped>
.wrapper {
	&.--element-below {
		border-bottom: 1px solid var(--gray-300);
		margin-bottom: var(--size-lg);
		padding-bottom: var(--size-lg);

		@media screen and (max-width: 992px) {
			padding-bottom: var(--size-md);
			margin-bottom: var(--size-md);
		}
	}
}

.order-summary {
	--product-img-size: 64px;

	background: var(--white);
	border-radius: var(--size-xs);
	border: 1px solid var(--gray-300);
	color: var(--gray-800);
	font-size: var(--size-base);
	font-weight: 500;
	padding: var(--size-md);

	&__product {
		display: flex;
		gap: var(--size-sm);
	}

	&__products {
		display: grid;
		gap: var(--size-sm);
		margin-bottom: var(--size-md);
	}

	&__img-container {
		width: var(--product-img-size);
		height: var(--product-img-size);
		border-radius: var(--size-xs);
		padding: var(--size-xs);
		border: 1px solid var(--gray-300);

		> img {
			object-fit: contain;
			width: 100%;
			height: 100%;
		}
	}

	&__product-name {
		flex: 1;
		font-weight: 600;
	}

	&__product-details {
		display: flex;
		gap: var(--size-sm);
		align-items: center;
		line-height: 1.4;
		flex: 1;
	}

	&__details {
		margin: 0;
		display: grid;
		grid-template-columns: auto 1fr;
		gap: var(--size-sm);
	}

	&__item {
		margin: 0;
		font-weight: 500;
		line-height: 1;
		display: flex;
		flex-direction: column;
		gap: var(--size-xs);

		&.--muted {
			color: var(--gray-500);
		}

		&.--total {
			font-size: var(--size-md);
			font-weight: 600;
		}

		&:nth-child(even) {
			text-align: right;
		}
	}

	&__quantity {
		position: absolute;
		right: -var(--size-sm);
		top: --var(--size-sm);
	}

	&__price {
		font-weight: 600;
		text-align: right;
	}

	&__currency {
		color: var(--gray-500);
		font-size: var(--size-base);
	}
}

.loading-skeleton {
	display: grid;
	gap: var(--size-base);
	grid-template-columns: 1fr 1fr;

	&__item {
		height: var(--size-base);
		border-radius: var(--size-sm);
		background: var(--gray-200);
		grid-column: span 2;
		position: relative;
		overflow: hidden;

		&:first-of-type {
			height: var(--product-img-size);
		}

		&.--half {
			grid-column: span 1;
		}

		&::after {
			will-change: transform;
			position: absolute;
			content: '';
			animation: shimmer 2s infinite;
			inset: 0;
			width: 100%;
			background: linear-gradient(
				to right,
				var(--gray-200) 4%,
				var(--gray-300) 25%,
				var(--gray-200) 36%
			);
		}
	}
}
</style>
